import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  collection,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import {
  Alert,
  Avatar,
  Box,
  Button,
  ButtonBase,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Snackbar,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import { auth, db } from "../../../firebase";
import { clearSignedOutUserStorage } from "../../../store/appStorage";
import {
  acceptConnectionRequest,
  declineConnectionRequest,
  fetchUserProfileByHandle,
  cancelConnectionRequest,
  fetchIncomingConnectionRequests,
  getLovedTimeCapsuleIds,
  getConnectionRequestStatus,
  removeFcmTokenFromFirestore,
  removeUserConnection,
  sendConnectionRequest,
  fetchUserProfileFromFirestore,
} from "../../../services/profileService";
import { fetchTimeCapsulesByIds } from "../../../services/timeCapsuleService";
import {
  fetchBusinessesByIds,
  subscribeSponsoredCapsulesForBusinessIds,
  subscribeFollowingBusinesses,
} from "../../../services/businessService";
import { fetchCampaignOffersForBusinesses, getCampaignOfferClaimPath } from "../../../services/offersService";
import useUserProfileStore, { selectClearUserProfile } from "../../../store/useUserProfileStore";
import {
  getProfilePathByUid,
  getPublicHandle,
  getPublicProfilePathByHandle,
  normalizePublicHandle,
} from "../../../utils/publicProfile";
import {
  getProfileHeroActionLabel,
  getProfileHeroBioText,
  getProfileHeroDisplayName,
} from "../../../utils/profileCompletionPrompt";
import { ellipsizeProfileText } from "../../../utils/profileText";
import { buildAvatarImageAttrs } from "../../../utils/imageUrl";
import { APP_SHELL_LAYOUT } from "../../../constants/constants";
import { useSeoRoute } from "../../../seo";
import { isRenderProfilingEnabled } from "../../../dev/renderProfiler";

const CAPSULE_TYPE_LABELS = {
  public: "Public",
  memberExclusive: "Member Exclusive",
  sponsored: "Sponsored",
};

const HERO_STAT_ITEMS = ({
  connections,
  requests,
  isOwnProfile,
}) => {
  if (!isOwnProfile) return [];

  return [
    { value: connections, label: "friends", to: "/profile/connections" },
    {
      value: requests,
      label: "requests",
      to: "/profile/connections/requests",
      highlight: requests > 0,
    },
  ];
};

function getOfferClaimPath(offer = {}) {
  return getCampaignOfferClaimPath(offer);
}

function renderAvatarFallback(...values) {
  const initial = values
    .map((value) => String(value || "").trim())
    .find(Boolean)
    ?.charAt(0);

  if (initial) {
    return initial.toUpperCase();
  }

  return <PersonRoundedIcon fontSize="small" />;
}

function getFallbackAvatarSx(hasImage) {
  if (hasImage) {
    return {};
  }

  return {
    bgcolor: (theme) =>
      theme.palette.mode === "dark" ? "rgba(92, 122, 255, 0.28)" : "#dbe5ff",
    color: (theme) =>
      theme.palette.mode === "dark" ? "#f4f7ff" : "#2341a1",
  };
}

function firstImageUrl(...values) {
  const flattened = values.flatMap((value) => {
    if (!value) return [];
    if (typeof value === "string") return [value];
    if (Array.isArray(value)) return value.flatMap((entry) => firstImageUrl(entry));
    if (typeof value === "object") {
      return [
        value.sessionPreviewUrl,
        value.posterUrl,
        value.thumbnailUrl,
        value.imageUrl,
        value.imageURL,
        value.photoUrl,
        value.photoURL,
        value.downloadURL,
        value.url,
      ];
    }
    return [];
  });
  return String(flattened.find((url) => String(url || "").trim()) || "").trim();
}

function normalizeOfferType(value = "") {
  return String(value || "").trim().toLowerCase() === "coupon" ? "coupon" : "raffle";
}

function buildFollowingContentItem(item = {}, kind = "capsule") {
  if (kind === "offer") {
    const type = normalizeOfferType(item?.campaignType);
    return {
      id: `offer-${item?.campaignId || item?.id}`,
      rawId: String(item?.campaignId || item?.id || "").trim(),
      businessId: String(item?.businessId || "").trim(),
      type,
      label: type === "coupon" ? "Coupon" : "Raffle",
      title: String(item?.title || (type === "coupon" ? "Coupon offer" : "Raffle entry")).trim(),
      href: getOfferClaimPath(item) || "/claims",
      imageUrl: firstImageUrl(item?.heroImageUrl, item?.thumbnailUrl, item?.imageUrl, item?.heroImage),
      when: item?.claimEndAt || item?.updatedAt || item?.createdAt || null,
    };
  }

  return {
    id: `capsule-${item?.id}`,
    rawId: String(item?.id || "").trim(),
    businessId: String(item?.businessId || item?.senderId || "").trim(),
    type: "capsule",
    label: "Featured",
    title: String(item?.messageTitle || item?.messageBody || "Featured time capsule").trim(),
    href: `/capsule/${encodeURIComponent(String(item?.id || "").trim())}`,
    imageUrl: firstImageUrl(
      item?.heroImageUrl,
      item?.thumbnailUrl,
      item?.imageUrl,
      item?.coverImageUrl,
      item?.media,
      item?.mediaFiles,
      item?.previewMedia,
      item?.images,
      item?.photos
    ),
    when: item?.sentAt || item?.createdAt || null,
  };
}

function buildLovedContentItem(capsule = {}) {
  return {
    id: String(capsule?.id || "").trim(),
    label: CAPSULE_TYPE_LABELS[capsule?.capsuleType] || "Time Capsule",
    title: String(capsule?.messageTitle || capsule?.messageBody || "Untitled Time Capsule").trim(),
    href: `/capsule/${encodeURIComponent(String(capsule?.id || "").trim())}`,
    imageUrl: firstImageUrl(
      capsule?.heroImageUrl,
      capsule?.thumbnailUrl,
      capsule?.imageUrl,
      capsule?.coverImageUrl,
      capsule?.media,
      capsule?.mediaFiles,
      capsule?.previewMedia,
      capsule?.images,
      capsule?.photos
    ),
  };
}

function SegmentedTabs({ value, options, onChange }) {
  return (
    <Tabs
      value={value}
      onChange={(_, nextValue) => onChange(nextValue)}
      variant="scrollable"
      scrollButtons="auto"
      allowScrollButtonsMobile
      sx={{ mb: 1.5, "& .MuiTab-root": { textTransform: "none" } }}
    >
      {options.map((option) => (
        <Tab key={option.value} value={option.value} label={option.label} />
      ))}
    </Tabs>
  );
}

function ProfileClaimOfferList({ title, emptyMessage, items, actionLabel }) {
  if (!items.length) {
    return <Alert severity="info">{emptyMessage}</Alert>;
  }

  return (
    <Stack spacing={1}>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between">
        <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
          {title}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 800 }}>
          Valid now
        </Typography>
      </Stack>
      <Stack spacing={1}>
        {items.map((item) => (
          <Card key={item.id} variant="outlined">
            <CardActionArea component={RouterLink} to={item.href} data-testid="profile-public-offer-card">
              <CardContent sx={{ p: 1.1, "&:last-child": { pb: 1.1 } }}>
                <Stack direction="row" spacing={1.2} alignItems="center">
                  <Box
                    sx={(theme) => ({
                      width: 74,
                      height: 74,
                      flexShrink: 0,
                      borderRadius: 1,
                      overflow: "hidden",
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? alpha(theme.palette.common.white, 0.08)
                          : alpha(theme.colors?.paleBlue || theme.colors?.mediumBlue || "#4164D8", 0.1),
                    })}
                  >
                    {item.imageUrl ? (
                      <Box
                        component="img"
                        src={item.imageUrl}
                        alt=""
                        sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : null}
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="caption"
                      sx={(theme) => ({
                        color: theme.palette.success.main,
                        fontWeight: 900,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                      })}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.2,
                        fontWeight: 900,
                        lineHeight: 1.25,
                        overflow: "hidden",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Box
                      component="span"
                      sx={(theme) => ({
                        display: "inline-flex",
                        mt: 0.8,
                        px: 1.1,
                        py: 0.45,
                        borderRadius: 999,
                        color: theme.palette.mode === "dark" ? "#07150e" : theme.palette.success.contrastText,
                        backgroundColor: theme.palette.success.main,
                        fontSize: "0.72rem",
                        fontWeight: 900,
                      })}
                    >
                      {actionLabel}
                    </Box>
                  </Box>
                </Stack>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Stack>
    </Stack>
  );
}

function ProfileTileGridSkeleton({ testId = "profile-grid-skeleton", label = "Loading profile content" }) {
  return (
    <Box
      data-testid={testId}
      role="status"
      aria-label={label}
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
        gap: "3px",
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      {Array.from({ length: 6 }).map((_, index) => (
        <Skeleton
          key={index}
          variant="rectangular"
          animation="wave"
          sx={{ aspectRatio: "1 / 1", height: "auto" }}
        />
      ))}
    </Box>
  );
}

function ProfileAlertsSkeleton() {
  return (
    <Stack data-testid="profile-alerts-skeleton" role="status" aria-label="Loading alerts" spacing={1}>
      {Array.from({ length: 3 }).map((_, index) => (
        <Box
          key={index}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            p: 1,
          }}
        >
          <Skeleton variant="circular" width={38} height={38} animation="wave" />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Skeleton variant="text" width="52%" animation="wave" />
            <Skeleton variant="text" width="36%" animation="wave" />
          </Box>
          <Skeleton variant="rounded" width={72} height={30} animation="wave" />
        </Box>
      ))}
    </Stack>
  );
}

function ProfileBusinessFeedSkeleton() {
  return (
    <Stack data-testid="profile-business-feed-skeleton" role="status" aria-label="Loading followed business updates" spacing={1.5}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
          gap: 1.25,
        }}
      >
        {Array.from({ length: 4 }).map((_, index) => (
          <Stack key={index} spacing={0.45} alignItems="center">
            <Skeleton variant="circular" width={56} height={56} animation="wave" />
            <Skeleton variant="text" width="78%" animation="wave" />
          </Stack>
        ))}
      </Box>
      <ProfileTileGridSkeleton testId="profile-business-content-skeleton" label="Loading business content" />
    </Stack>
  );
}

function ProfileOfferListSkeleton({ testId = "profile-offer-list-skeleton", label = "Loading offers" }) {
  return (
    <Stack data-testid={testId} role="status" aria-label={label} spacing={1}>
      {Array.from({ length: 3 }).map((_, index) => (
        <Box
          key={index}
          sx={{
            display: "flex",
            gap: 1.2,
            alignItems: "center",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            p: 1.1,
          }}
        >
          <Skeleton variant="rounded" width={74} height={74} animation="wave" />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Skeleton variant="text" width="24%" animation="wave" />
            <Skeleton variant="text" width="78%" animation="wave" />
            <Skeleton variant="rounded" width={68} height={24} sx={{ mt: 0.8 }} animation="wave" />
          </Box>
        </Box>
      ))}
    </Stack>
  );
}

function tsToMillis(ts) {
  return (
    ts?.toMillis?.() ||
    (typeof ts?.seconds === "number" ? ts.seconds * 1000 : 0)
  );
}

function formatWhen(ts) {
  const ms = tsToMillis(ts);
  if (!ms) return "now";
  const diff = Date.now() - ms;
  if (diff < 60 * 1000) return "now";
  const mins = Math.floor(diff / (60 * 1000));
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

function createProfileLoadTimer(label) {
  if (!isRenderProfilingEnabled()) {
    return {
      mark: () => {},
      done: () => {},
    };
  }

  const startedAt =
    typeof performance !== "undefined" && typeof performance.now === "function"
      ? performance.now()
      : Date.now();
  const elapsedMs = () => {
    const now =
      typeof performance !== "undefined" && typeof performance.now === "function"
        ? performance.now()
        : Date.now();
    return Math.round(now - startedAt);
  };

  console.log(`[profile-load] ${label} start`);

  return {
    mark: (phase, details = {}) => {
      console.log(`[profile-load] ${label} ${phase}`, {
        elapsedMs: elapsedMs(),
        ...details,
      });
    },
    done: (phase = "done", details = {}) => {
      console.log(`[profile-load] ${label} ${phase}`, {
        elapsedMs: elapsedMs(),
        ...details,
      });
    },
  };
}

export default function PublicProfilePage() {
  const { handle } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [profileLoading, setProfileLoading] = useState(true);
  const [error, setError] = useState("");
  const [profile, setProfile] = useState(null);
  const [lovedCapsules, setLovedCapsules] = useState([]);
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [incomingRequestCount, setIncomingRequestCount] = useState(0);
  const [unreadCommentCapsules, setUnreadCommentCapsules] = useState([]);
  const [unreadMentionActivities, setUnreadMentionActivities] = useState([]);
  const [followingBusinesses, setFollowingBusinesses] = useState([]);
  const [followingBusinessProfiles, setFollowingBusinessProfiles] = useState({});
  const [followedBusinessCapsules, setFollowedBusinessCapsules] = useState([]);
  const [followedBusinessOffers, setFollowedBusinessOffers] = useState([]);
  const [publicProfileFollowingBusinesses, setPublicProfileFollowingBusinesses] = useState([]);
  const [publicProfileOffers, setPublicProfileOffers] = useState([]);
  const [subcollectionConnectionIds, setSubcollectionConnectionIds] = useState([]);
  const [notificationsSnackbar, setNotificationsSnackbar] = useState("");
  const [ownProfileTab, setOwnProfileTab] = useState("businessFeed");
  const [publicProfileTab, setPublicProfileTab] = useState("loved");
  const [connectionRequestState, setConnectionRequestState] = useState({ incoming: false, outgoing: false });
  const [connectionBusy, setConnectionBusy] = useState(false);
  const [unfriendConfirmOpen, setUnfriendConfirmOpen] = useState(false);
  const [lovedLoading, setLovedLoading] = useState(false);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [connectionLoading, setConnectionLoading] = useState(false);
  const [followingBusinessesLoading, setFollowingBusinessesLoading] = useState(false);
  const [followingBusinessProfilesLoading, setFollowingBusinessProfilesLoading] = useState(false);
  const [followedBusinessCapsulesLoading, setFollowedBusinessCapsulesLoading] = useState(false);
  const [followedBusinessOffersLoading, setFollowedBusinessOffersLoading] = useState(false);
  const [publicFollowingBusinessesLoading, setPublicFollowingBusinessesLoading] = useState(false);
  const [publicOffersLoading, setPublicOffersLoading] = useState(false);
  const capsulesSectionHighlighted = false;

  const currentUser = auth.currentUser;
  const currentUserUid = String(currentUser?.uid || "").trim();
  const clearUserProfile = useUserProfileStore(selectClearUserProfile);
  const isOwnProfile = Boolean(currentUserUid && profile?.uid === currentUserUid);
  const isPublicPersonProfile = Boolean(profile?.uid) && !isOwnProfile;
  const fallbackUid = new URLSearchParams(location.search).get("uid") || "";
  const lovedCapsuleIds = useMemo(() => getLovedTimeCapsuleIds(profile || {}), [profile]);
  const lovedCapsuleIdsKey = lovedCapsuleIds.join("|");

  useSeoRoute(
    profile
      ? {
          id: profile.uid || fallbackUid,
          handle: profile.publicHandle || handle,
          publicHandle: profile.publicHandle || handle,
          displayName: profile.displayName || profile.userName || "",
          userName: profile.userName || "",
          description: profile.bio || "",
          image: profile.profileImage || "",
        }
      : {
          id: fallbackUid,
          handle,
          publicHandle: handle,
        }
  );

  useEffect(() => {
    let isActive = true;

    async function loadProfile() {
      const timer = createProfileLoadTimer("profile identity");
      setProfileLoading(true);
      setError("");
      setProfile(null);
      setLovedCapsules([]);
      setIncomingRequests([]);
      setIncomingRequestCount(0);
      setConnectionRequestState({ incoming: false, outgoing: false });
      setLovedLoading(false);
      setRequestsLoading(false);
      setConnectionLoading(false);

      try {
        timer.mark("handle lookup start", { handle });
        let nextProfile = await fetchUserProfileByHandle(handle);
        timer.mark("handle lookup complete", { found: Boolean(nextProfile) });

        if (!nextProfile && fallbackUid) {
          timer.mark("fallback uid lookup start", { fallbackUid });
          const fallbackProfile = await fetchUserProfileFromFirestore(fallbackUid);
          if (fallbackProfile) {
            nextProfile = { uid: fallbackUid, ...fallbackProfile };
          }
          timer.mark("fallback uid lookup complete", { found: Boolean(nextProfile) });
        }
        if (!isActive) return;

        if (!nextProfile) {
          setProfile(null);
          setLovedCapsules([]);
          setIncomingRequests([]);
          setIncomingRequestCount(0);
          setConnectionRequestState({ incoming: false, outgoing: false });
          setError("Profile not found.");
          return;
        }

        const isSelf = currentUserUid === nextProfile.uid;
        if (nextProfile.isDiscoverable === false && !isSelf) {
          setProfile(null);
          setLovedCapsules([]);
          setIncomingRequests([]);
          setIncomingRequestCount(0);
          setConnectionRequestState({ incoming: false, outgoing: false });
          setError("This profile is not public.");
          return;
        }

        setProfile(nextProfile);
        timer.done("profile ready", { profileUid: nextProfile.uid, isSelf });
      } catch (err) {
        console.error("Failed to load public profile:", err);
        if (!isActive) return;
        setProfile(null);
        setLovedCapsules([]);
        setIncomingRequests([]);
        setIncomingRequestCount(0);
        setConnectionRequestState({ incoming: false, outgoing: false });
        setError("Failed to load profile.");
      } finally {
        if (isActive) setProfileLoading(false);
      }
    }

    loadProfile();

    return () => {
      isActive = false;
    };
  }, [currentUserUid, fallbackUid, handle]);

  useEffect(() => {
    const profileUid = String(profile?.uid || "").trim();
    if (!profileUid || isOwnProfile) {
      setLovedCapsules([]);
      setLovedLoading(false);
      return undefined;
    }

    let active = true;
    const timer = createProfileLoadTimer("loved capsules");
    setLovedLoading(true);
    fetchTimeCapsulesByIds(lovedCapsuleIds, currentUserUid)
      .then((rows) => {
        if (!active) return;
        setLovedCapsules(Array.isArray(rows) ? rows : []);
        timer.done("resolved", { count: Array.isArray(rows) ? rows.length : 0 });
      })
      .catch((err) => {
        console.error("Failed to load loved capsules for profile:", err);
        if (active) setLovedCapsules([]);
        timer.done("failed");
      })
      .finally(() => {
        if (active) setLovedLoading(false);
      });

    return () => {
      active = false;
    };
  }, [currentUserUid, isOwnProfile, lovedCapsuleIds, lovedCapsuleIdsKey, profile?.uid]);

  useEffect(() => {
    if (!isOwnProfile || !currentUserUid) {
      setIncomingRequests([]);
      setIncomingRequestCount(0);
      setRequestsLoading(false);
      return undefined;
    }

    let active = true;
    const timer = createProfileLoadTimer("incoming requests");
    setRequestsLoading(true);
    fetchIncomingConnectionRequests(currentUserUid)
      .then((rows) => {
        const nextRows = Array.isArray(rows) ? rows : [];
        if (!active) return;
        setIncomingRequests(nextRows);
        setIncomingRequestCount(nextRows.length);
        timer.done("resolved", { count: nextRows.length });
      })
      .catch((err) => {
        console.error("Failed to load incoming connection requests:", err);
        if (active) {
          setIncomingRequests([]);
          setIncomingRequestCount(0);
        }
        timer.done("failed");
      })
      .finally(() => {
        if (active) setRequestsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [currentUserUid, isOwnProfile]);

  useEffect(() => {
    const profileUid = String(profile?.uid || "").trim();
    if (!profileUid || isOwnProfile || !currentUserUid) {
      setConnectionRequestState({ incoming: false, outgoing: false });
      setConnectionLoading(false);
      return undefined;
    }

    let active = true;
    const timer = createProfileLoadTimer("connection status");
    setConnectionLoading(true);
    getConnectionRequestStatus(currentUserUid, profileUid)
      .then((requestState) => {
        if (!active) return;
        setConnectionRequestState(requestState || { incoming: false, outgoing: false });
        timer.done("resolved");
      })
      .catch((err) => {
        console.error("Failed to load connection request state:", err);
        if (active) setConnectionRequestState({ incoming: false, outgoing: false });
        timer.done("failed");
      })
      .finally(() => {
        if (active) setConnectionLoading(false);
      });

    return () => {
      active = false;
    };
  }, [currentUserUid, isOwnProfile, profile?.uid]);

  const followedBusinessIds = useMemo(
    () =>
      Array.from(
        new Set(
          (Array.isArray(followingBusinesses) ? followingBusinesses : [])
            .map((row) => String(row?.businessId || row?.id || "").trim())
            .filter(Boolean)
        )
      ),
    [followingBusinesses]
  );
  const followedBusinessIdsKey = followedBusinessIds.join("|");
  const publicProfileFollowedBusinessIds = useMemo(
    () =>
      Array.from(
        new Set(
          (Array.isArray(publicProfileFollowingBusinesses) ? publicProfileFollowingBusinesses : [])
            .map((row) => String(row?.businessId || row?.id || "").trim())
            .filter(Boolean)
        )
      ),
    [publicProfileFollowingBusinesses]
  );
  const publicProfileFollowedBusinessIdsKey = publicProfileFollowedBusinessIds.join("|");

  useEffect(() => {
    if (isOwnProfile || !profile?.uid) {
      return undefined;
    }

    const userRef = doc(db, "users", profile.uid);
    const unsubscribe = onSnapshot(
      userRef,
      (snap) => {
        if (!snap.exists()) {
          return;
        }

        setProfile((prev) => ({ ...(prev || {}), uid: snap.id, ...snap.data() }));
      },
      (err) => {
        console.error("Failed to subscribe to profile updates:", err);
      }
    );

    return unsubscribe;
  }, [isOwnProfile, profile?.uid]);

  useEffect(() => {
    const profileUid = String(profile?.uid || "").trim();
    if (!profileUid) {
      setSubcollectionConnectionIds([]);
      return undefined;
    }

    const connectionsRef = collection(db, "users", profileUid, "connections");
    const unsubscribe = onSnapshot(
      connectionsRef,
      (snap) => {
        const nextIds = snap.docs.map((docSnap) => String(docSnap.id || "").trim()).filter(Boolean);
        setSubcollectionConnectionIds(nextIds);
      },
      (err) => {
        console.error("Failed to subscribe to profile connections subcollection:", err);
        setSubcollectionConnectionIds([]);
      }
    );

    return unsubscribe;
  }, [profile?.uid]);

  useEffect(() => {
    if (!isOwnProfile || !currentUserUid) {
      setFollowingBusinesses([]);
      setFollowingBusinessesLoading(false);
      return undefined;
    }

    const timer = createProfileLoadTimer("following businesses");
    setFollowingBusinessesLoading(true);
    return subscribeFollowingBusinesses(currentUserUid, (rows) => {
      setFollowingBusinesses(Array.isArray(rows) ? rows : []);
      setFollowingBusinessesLoading(false);
      timer.done("snapshot", { count: Array.isArray(rows) ? rows.length : 0 });
    }, () => {
      setFollowingBusinessesLoading(false);
      timer.done("failed");
    });
  }, [currentUserUid, isOwnProfile]);

  useEffect(() => {
    if (!isPublicPersonProfile || !profile?.uid) {
      setPublicProfileFollowingBusinesses([]);
      setPublicFollowingBusinessesLoading(false);
      return undefined;
    }

    const timer = createProfileLoadTimer("public following businesses");
    setPublicFollowingBusinessesLoading(true);
    return subscribeFollowingBusinesses(
      profile.uid,
      (rows) => {
        setPublicProfileFollowingBusinesses(Array.isArray(rows) ? rows : []);
        setPublicFollowingBusinessesLoading(false);
        timer.done("snapshot", { count: Array.isArray(rows) ? rows.length : 0 });
      },
      () => {
        setPublicProfileFollowingBusinesses([]);
        setPublicFollowingBusinessesLoading(false);
        timer.done("failed");
      }
    );
  }, [isPublicPersonProfile, profile?.uid]);

  useEffect(() => {
    if (!isOwnProfile || !followedBusinessIds.length) {
      setFollowingBusinessProfiles({});
      setFollowingBusinessProfilesLoading(false);
      return undefined;
    }

    let active = true;
    const timer = createProfileLoadTimer("business profiles");
    setFollowingBusinessProfilesLoading(true);
    fetchBusinessesByIds(followedBusinessIds)
      .then((result) => {
        if (!active) return;
        setFollowingBusinessProfiles(result || {});
        timer.done("resolved", { count: Object.keys(result || {}).length });
      })
      .catch((err) => {
        console.error("Failed to load followed business profiles:", err);
        if (active) setFollowingBusinessProfiles({});
        timer.done("failed");
      })
      .finally(() => {
        if (active) setFollowingBusinessProfilesLoading(false);
      });

    return () => {
      active = false;
    };
  }, [followedBusinessIds, followedBusinessIdsKey, isOwnProfile]);

  useEffect(() => {
    if (!isOwnProfile || !followedBusinessIds.length) {
      setFollowedBusinessCapsules([]);
      setFollowedBusinessCapsulesLoading(false);
      return undefined;
    }

    const timer = createProfileLoadTimer("followed business capsules");
    setFollowedBusinessCapsulesLoading(true);
    return subscribeSponsoredCapsulesForBusinessIds(
      followedBusinessIds,
      (rows) => {
        setFollowedBusinessCapsules(Array.isArray(rows) ? rows : []);
        setFollowedBusinessCapsulesLoading(false);
        timer.done("snapshot", { count: Array.isArray(rows) ? rows.length : 0 });
      },
      { limit: 60 }
    );
  }, [followedBusinessIds, followedBusinessIdsKey, isOwnProfile]);

  useEffect(() => {
    if (!isOwnProfile || !followedBusinessIds.length) {
      setFollowedBusinessOffers([]);
      setFollowedBusinessOffersLoading(false);
      return undefined;
    }

    let active = true;
    const timer = createProfileLoadTimer("followed business offers");
    setFollowedBusinessOffersLoading(true);
    fetchCampaignOffersForBusinesses(followedBusinessIds, { limit: 60 })
      .then((rows) => {
        if (!active) return;
        setFollowedBusinessOffers(Array.isArray(rows) ? rows : []);
        timer.done("resolved", { count: Array.isArray(rows) ? rows.length : 0 });
      })
      .catch((err) => {
        console.error("Failed to load followed business offers:", err);
        if (active) setFollowedBusinessOffers([]);
        timer.done("failed");
      })
      .finally(() => {
        if (active) setFollowedBusinessOffersLoading(false);
      });

    return () => {
      active = false;
    };
  }, [followedBusinessIds, followedBusinessIdsKey, isOwnProfile]);

  useEffect(() => {
    if (!isPublicPersonProfile || !publicProfileFollowedBusinessIds.length) {
      setPublicProfileOffers([]);
      setPublicOffersLoading(false);
      return undefined;
    }

    let active = true;
    const timer = createProfileLoadTimer("public profile offers");
    setPublicOffersLoading(true);
    fetchCampaignOffersForBusinesses(publicProfileFollowedBusinessIds, { limit: 60 })
      .then((rows) => {
        if (!active) return;
        setPublicProfileOffers(Array.isArray(rows) ? rows : []);
        timer.done("resolved", { count: Array.isArray(rows) ? rows.length : 0 });
      })
      .catch((err) => {
        console.error("Failed to load public profile offers:", err);
        if (active) setPublicProfileOffers([]);
        timer.done("failed");
      })
      .finally(() => {
        if (active) setPublicOffersLoading(false);
      });

    return () => {
      active = false;
    };
  }, [isPublicPersonProfile, publicProfileFollowedBusinessIds, publicProfileFollowedBusinessIdsKey]);

  useEffect(() => {
    // Unread-comment markers belong to the person who holds them. The capsule
    // list rule admits `unreadCommentUserIds array-contains <me>` and denies
    // `array-contains <someone else>`, which is correct: a visitor has no
    // business enumerating another profile's unread threads. So this only runs
    // when you are looking at your own profile.
    const viewerUid = auth.currentUser?.uid || "";
    if (!viewerUid || viewerUid !== profile?.uid) {
      setUnreadCommentCapsules([]);
      return undefined;
    }

    const unreadCommentsQ = query(
      collection(db, "timeCapsules"),
      where("unreadCommentUserIds", "array-contains", viewerUid)
    );

    const unsub = onSnapshot(
      unreadCommentsQ,
      (snap) => {
        const rows = snap.docs
          .map((docSnap) => {
            const data = docSnap.data() || {};
            return {
              capsuleId: docSnap.id,
              title: String(data.messageTitle || "").trim() || "Untitled capsule",
              when:
                data.lastCommentAt ||
                data.updatedAt ||
                data.sentAt ||
                data.createdAt ||
                null,
            };
          })
          .sort((a, b) => tsToMillis(b.when) - tsToMillis(a.when));

        setUnreadCommentCapsules(rows);
      },
      (err) => {
        console.error("Failed to subscribe unread comment activity on profile:", err);
      }
    );

    return () => unsub();
  }, [isOwnProfile, profile?.uid]);

  useEffect(() => {
    if (!isOwnProfile || !profile?.uid) {
      setUnreadMentionActivities([]);
      return undefined;
    }

    const mentionsQ = query(
      collection(db, "users", profile.uid, "activity"),
      where("isRead", "==", false)
    );

    const unsub = onSnapshot(
      mentionsQ,
      (snap) => {
        const rows = snap.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .filter((row) => String(row?.type || "").trim() === "capsuleMention")
          .map((row) => ({
            id: String(row.id || "").trim(),
            capsuleId: String(row.capsuleId || "").trim(),
            title: String(row.capsuleTitle || "").trim() || "Untitled capsule",
            actorDisplayName: String(row.actorDisplayName || "").trim() || "Someone",
            when: row.createdAt || row.updatedAt || null,
          }))
          .filter((row) => row.id && row.capsuleId)
          .sort((a, b) => tsToMillis(b.when) - tsToMillis(a.when));

        setUnreadMentionActivities(rows);
      },
      (err) => {
        console.error("Failed to subscribe unread mention activity on profile:", err);
      }
    );

    return () => unsub();
  }, [isOwnProfile, profile?.uid]);

  const visibleHandle =
    normalizePublicHandle(profile?.publicHandle || "") ||
    getPublicHandle(profile?.userName);
  const profileDisplayName = getProfileHeroDisplayName(profile, isOwnProfile, visibleHandle);
  const profileBioText = getProfileHeroBioText(profile, isOwnProfile);
  const profileActionLabel = getProfileHeroActionLabel(profile, isOwnProfile);
  const profileAvatarAttrs = buildAvatarImageAttrs(profile?.profileImage, { size: 72 });
  const connectionIds = useMemo(() => {
    const userDocConnectionIds = Array.isArray(profile?.connections)
      ? profile.connections.map((id) => String(id || "").trim()).filter(Boolean)
      : [];
    return Array.from(new Set([...userDocConnectionIds, ...subcollectionConnectionIds]));
  }, [profile?.connections, subcollectionConnectionIds]);
  const connectionCount = connectionIds.length;
  const isConnected = Boolean(currentUser?.uid && connectionIds.includes(currentUser.uid));
  const hasIncomingRequest = Boolean(connectionRequestState.incoming);
  const hasOutgoingRequest = Boolean(connectionRequestState.outgoing);
  const followingContentItems = useMemo(() => {
    const offerItems = (Array.isArray(followedBusinessOffers) ? followedBusinessOffers : [])
      .map((item) => buildFollowingContentItem(item, "offer"))
      .filter((item) => item.rawId);
    const capsuleItems = (Array.isArray(followedBusinessCapsules) ? followedBusinessCapsules : [])
      .map((item) => buildFollowingContentItem(item, "capsule"))
      .filter((item) => item.rawId);
    return [...offerItems, ...capsuleItems].sort((a, b) => tsToMillis(b.when) - tsToMillis(a.when));
  }, [followedBusinessCapsules, followedBusinessOffers]);
  const followedBusinessCards = useMemo(() => {
    const rowsById = (Array.isArray(followingBusinesses) ? followingBusinesses : []).reduce((acc, row) => {
      const id = String(row?.businessId || row?.id || "").trim();
      if (id) acc[id] = row;
      return acc;
    }, {});

    return followedBusinessIds.map((businessId) => {
      const profile = followingBusinessProfiles[businessId] || {};
      const row = rowsById[businessId] || {};
      const content = followingContentItems.filter((item) => item.businessId === businessId);
      const counts = content.reduce(
        (acc, item) => {
          acc.total += 1;
          acc[item.type] = (acc[item.type] || 0) + 1;
          return acc;
        },
        { total: 0, coupon: 0, raffle: 0, capsule: 0 }
      );
      return {
        id: businessId,
        name: String(profile.displayName || profile.companyName || row.businessName || "Business").trim(),
        avatarUrl: String(profile.avatarUrl || profile.logoUrl || row.businessAvatarUrl || "").trim(),
        counts,
      };
    });
  }, [followedBusinessIds, followingBusinessProfiles, followingBusinesses, followingContentItems]);
  const publicLovedContentItems = useMemo(
    () =>
      (Array.isArray(lovedCapsules) ? lovedCapsules : [])
        .filter((capsule) => String(capsule?.capsuleType || "").trim() === "public")
        .map(buildLovedContentItem)
        .filter((item) => item.id),
    [lovedCapsules]
  );
  const publicProfileOfferItems = useMemo(
    () =>
      (Array.isArray(publicProfileOffers) ? publicProfileOffers : [])
        .map((item) => buildFollowingContentItem(item, "offer"))
        .filter((item) => item.rawId),
    [publicProfileOffers]
  );
  const publicProfileCouponItems = useMemo(
    () => publicProfileOfferItems.filter((item) => item.type === "coupon"),
    [publicProfileOfferItems]
  );
  const publicProfileRaffleItems = useMemo(
    () => publicProfileOfferItems.filter((item) => item.type === "raffle"),
    [publicProfileOfferItems]
  );
  const followingBusinessCount = followedBusinessCards.length || followedBusinessIds.length;
  const businessFeedLoading =
    followingBusinessesLoading ||
    followingBusinessProfilesLoading ||
    followedBusinessCapsulesLoading ||
    followedBusinessOffersLoading;
  const publicOfferTabsLoading = publicFollowingBusinessesLoading || publicOffersLoading;
  const notificationsUnreadCount =
    incomingRequestCount + unreadCommentCapsules.length + unreadMentionActivities.length;
  const ownProfileTabOptions = useMemo(
    () => [
      {
        value: "businessFeed",
        label: `Following${followingBusinessCount ? ` (${followingBusinessCount})` : ""}`,
      },
      { value: "notifications", label: `Alerts (${notificationsUnreadCount})` },
    ],
    [followingBusinessCount, notificationsUnreadCount]
  );
  const publicProfileTabOptions = useMemo(
    () => [
      { value: "loved", label: `Loved (${publicLovedContentItems.length})` },
      { value: "coupons", label: `Coupons (${publicProfileCouponItems.length})` },
      { value: "raffles", label: `Raffles (${publicProfileRaffleItems.length})` },
    ],
    [publicLovedContentItems.length, publicProfileCouponItems.length, publicProfileRaffleItems.length]
  );
  const statItems = HERO_STAT_ITEMS({
    connections: connectionCount,
    requests: incomingRequestCount,
    isOwnProfile,
  });

  const handleConfirmUnfriend = async () => {
    if (!currentUser || !profile || isOwnProfile || connectionBusy) return;

    setConnectionBusy(true);
    try {
      await removeUserConnection(currentUser.uid, profile.uid);
      setProfile((prev) =>
        prev
          ? {
              ...prev,
              connections: (prev.connections || []).filter((id) => id !== currentUser.uid),
            }
          : prev
      );
      setSubcollectionConnectionIds((prev) => prev.filter((id) => id !== currentUser.uid));
      setUnfriendConfirmOpen(false);
    } catch (err) {
      console.error("Failed to update connection state:", err);
    } finally {
      setConnectionBusy(false);
    }
  };

  const handleConnectionToggle = async () => {
    if (!currentUser || !profile || isOwnProfile || connectionBusy) return;

    if (isConnected) {
      setUnfriendConfirmOpen(true);
      return;
    }

    setConnectionBusy(true);
    try {
      if (hasIncomingRequest) {
        await acceptConnectionRequest(currentUser.uid, profile.uid);
        setProfile((prev) =>
          prev
            ? {
                ...prev,
                connections: Array.from(new Set([...(prev.connections || []), currentUser.uid])),
              }
            : prev
        );
        setConnectionRequestState({ incoming: false, outgoing: false });
      } else if (hasOutgoingRequest) {
        await cancelConnectionRequest(currentUser.uid, profile.uid);
        setConnectionRequestState({ incoming: false, outgoing: false });
      } else {
        const result = await sendConnectionRequest(currentUser, profile);
        if (result?.autoAccepted) {
          setProfile((prev) =>
            prev
              ? {
                  ...prev,
                  connections: Array.from(new Set([...(prev.connections || []), currentUser.uid])),
                }
              : prev
          );
          setConnectionRequestState({ incoming: false, outgoing: false });
        } else {
          setConnectionRequestState({ incoming: false, outgoing: true });
        }
      }
    } catch (err) {
      console.error("Failed to update connection state:", err);
    } finally {
      setConnectionBusy(false);
    }
  };

  const handleSignOut = useCallback(async () => {
    const SIGNIN_REDIRECT_KEY = "postAuthRedirectTo";
    const REDIRECT_CTX_KEY = "redirectCtx";
    const LOGGING_OUT_FLAG = "loggingOutNow";

    try {
      try {
        sessionStorage.setItem(LOGGING_OUT_FLAG, "1");
      } catch {}

      navigate("/", { replace: true, state: { clearFrom: true } });

      if (currentUser && navigator.onLine) {
        await removeFcmTokenFromFirestore(currentUser.uid);
      } else if (!navigator.onLine) {
        console.warn("User is offline. Skipping FCM token removal.");
      }

      await auth.signOut();
      clearUserProfile();
      clearSignedOutUserStorage();

      try {
        const keys = Object.keys(sessionStorage);
        keys.forEach((key) => {
          if (key.startsWith("hasShownSuccess-") || key.startsWith("hasShownSuccess")) {
            sessionStorage.removeItem(key);
          }
        });
        sessionStorage.removeItem(SIGNIN_REDIRECT_KEY);
        sessionStorage.removeItem(REDIRECT_CTX_KEY);
      } catch (e) {
        console.warn("Failed to clear logout session keys:", e);
      }
    } catch (err) {
      console.error("Error during logout:", err);
    } finally {
      window.setTimeout(() => {
        try {
          sessionStorage.removeItem(LOGGING_OUT_FLAG);
        } catch {}
      }, 500);
    }
  }, [clearUserProfile, currentUser, navigate]);

  const handleAcceptIncomingRequest = async (row) => {
    if (!currentUser?.uid || !row?.uid) return;
    try {
      await acceptConnectionRequest(currentUser.uid, row.uid);
      setIncomingRequests((prev) => prev.filter((request) => request.uid !== row.uid));
      setIncomingRequestCount((prev) => Math.max(0, prev - 1));
      setNotificationsSnackbar("Connection request accepted.");
    } catch (err) {
      console.error("Failed to accept incoming request from profile notifications:", err);
      setNotificationsSnackbar("Failed to accept request.");
    }
  };

  const handleDeclineIncomingRequest = async (row) => {
    if (!currentUser?.uid || !row?.uid) return;
    try {
      await declineConnectionRequest(currentUser.uid, row.uid);
      setIncomingRequests((prev) => prev.filter((request) => request.uid !== row.uid));
      setIncomingRequestCount((prev) => Math.max(0, prev - 1));
      setNotificationsSnackbar("Connection request declined.");
    } catch (err) {
      console.error("Failed to decline incoming request from profile notifications:", err);
      setNotificationsSnackbar("Failed to decline request.");
    }
  };

  const handleOpenMentionActivity = useCallback(
    async (item) => {
      if (!item?.capsuleId) return;
      const viewerUid = String(profile?.uid || "").trim();
      if (viewerUid && item.id) {
        try {
          await updateDoc(doc(db, "users", viewerUid, "activity", item.id), {
            isRead: true,
            readAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
        } catch (err) {
          console.error("Failed to mark mention activity as read:", err);
        }
      }

      navigate(`/capsule/${item.capsuleId}`, { state: { source: "profile-notifications" } });
    },
    [navigate, profile?.uid]
  );

  if (profileLoading) {
    return (
      <Box sx={{ minHeight: "50vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!profile) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="info">{error || "Profile not found."}</Alert>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="sm"
      sx={{
        pt: { xs: 2, sm: 3 },
        pb: `calc(${APP_SHELL_LAYOUT.BOTTOM_NAV_OFFSET_CSS} + 16px)`,
      }}
    >
      <Box
        sx={{
          p: { xs: 2, sm: 2.5 },
          borderRadius: 1,
          background: (theme) =>
            theme.palette.mode === "dark"
              ? "linear-gradient(180deg, rgba(44,34,74,0.92), rgba(21,18,40,0.96))"
              : "linear-gradient(180deg, rgba(248,249,255,0.98), rgba(242,246,255,0.98))",
          border: "1px solid",
          borderColor: (theme) =>
            theme.palette.mode === "dark" ? "rgba(170,180,196,0.18)" : "rgba(38,19,74,0.08)",
          boxShadow: (theme) =>
            theme.palette.mode === "dark"
              ? "0 18px 44px rgba(0,0,0,0.28)"
              : "0 18px 44px rgba(38,19,74,0.08)",
        }}
      >
        <Stack direction="row" spacing={1.75} alignItems="center">
          <Avatar
            src={profileAvatarAttrs.src || undefined}
            imgProps={{
              srcSet: profileAvatarAttrs.srcSet || undefined,
              sizes: profileAvatarAttrs.sizes || undefined,
              loading: "eager",
              decoding: "async",
            }}
            sx={{
              width: 72,
              height: 72,
              fontSize: 28,
              flexShrink: 0,
              border: "2px solid",
              borderColor: (theme) =>
                theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(38,19,74,0.08)",
              ...getFallbackAvatarSx(Boolean(profile.profileImage)),
            }}
          >
            {renderAvatarFallback(
              profile.displayName,
              normalizePublicHandle(profile.publicHandle || ""),
              profile.userName
            )}
          </Avatar>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                lineHeight: 1.05,
                fontSize: { xs: "2rem", sm: "2.25rem" },
                maxWidth: "100%",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
            >
              {profileDisplayName}
            </Typography>
            {visibleHandle && (
              <Typography
                variant="body2"
                sx={{ mt: 0.5, color: "text.secondary", fontWeight: 600, letterSpacing: "0.02em" }}
              >
                @{ellipsizeProfileText(visibleHandle)}
              </Typography>
            )}
          </Box>
        </Stack>

        {profileBioText && (
          <Typography
            variant="body2"
            sx={{ mt: 1.75, color: "text.secondary", whiteSpace: "pre-wrap", lineHeight: 1.6 }}
          >
            {profileBioText}
          </Typography>
        )}

        {statItems.length ? (
          <Box
            sx={{
              mt: 2,
              display: "grid",
              gridTemplateColumns:
                statItems.length === 1 ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))",
              gap: 1,
            }}
          >
            {statItems.map((item) => (
              <Box
                key={item.label}
                component={
                  item.to
                    ? item.to.startsWith("/")
                      ? RouterLink
                      : "button"
                    : "div"
                }
                to={item.to && item.to.startsWith("/") ? item.to : undefined}
                type={item.to && !item.to.startsWith("/") ? "button" : undefined}
                sx={{
                  display: "block",
                  p: 1,
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: (theme) =>
                    item.highlight
                      ? alpha(theme.palette.success.main, 0.5)
                      : theme.palette.mode === "dark"
                        ? "rgba(170,180,196,0.12)"
                        : "rgba(38,19,74,0.08)",
                  backgroundColor: (theme) =>
                    item.highlight
                      ? alpha(theme.palette.success.main, theme.palette.mode === "dark" ? 0.14 : 0.1)
                      : theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.03)"
                        : "rgba(255,255,255,0.72)",
                  textDecoration: "none",
                  color: "inherit",
                  width: "100%",
                  appearance: "none",
                  WebkitAppearance: "none",
                  borderWidth: "1px",
                  textAlign: "left",
                  font: "inherit",
                  transition: "background-color 160ms ease, border-color 160ms ease",
                  "&:hover": item.to
                    ? {
                        backgroundColor: (theme) =>
                          item.highlight
                            ? theme.palette.mode === "dark"
                              ? "rgba(86,195,114,0.16)"
                              : "rgba(86,195,114,0.16)"
                            : theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.05)"
                              : "rgba(255,255,255,0.92)",
                        borderColor: (theme) =>
                          item.highlight
                            ? alpha(theme.palette.success.main, 0.58)
                            : theme.palette.mode === "dark"
                              ? "rgba(170,180,196,0.2)"
                              : "rgba(38,19,74,0.14)",
                      }
                    : undefined,
                  cursor: item.to ? "pointer" : "default",
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 900, lineHeight: 1.2 }}>
                  <Box component="span" sx={{ fontSize: "1.15rem" }}>
                    {item.value}
                  </Box>{" "}
                  {item.label}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : null}

        <Divider sx={{ my: 2, borderColor: "divider" }} />

        <Stack spacing={1.1}>
          {isOwnProfile ? (
            <>
              <Button
                component={RouterLink}
                to="/profile/edit"
                variant="contained"
                fullWidth
                sx={(theme) => ({
                  minHeight: 44,
                  borderRadius: "999px",
                  fontWeight: 700,
                  backgroundColor: theme.colors.blue,
                  color: theme.colors.white,
                  "&:hover": {
                    backgroundColor: theme.colors.darkBlue || theme.colors.blue,
                  },
                })}
              >
                {profileActionLabel}
              </Button>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  variant="text"
                  color="error"
                  onClick={handleSignOut}
                  sx={{
                    minHeight: 32,
                    borderRadius: "999px",
                    fontWeight: 700,
                    textTransform: "none",
                    px: 1.25,
                  }}
                >
                  Sign Out
                </Button>
              </Box>
            </>
          ) : currentUser ? (
            <Button
              variant={isConnected || hasOutgoingRequest ? "outlined" : "contained"}
              onClick={handleConnectionToggle}
              disabled={connectionBusy || connectionLoading}
              fullWidth
              sx={{ borderRadius: "999px", minHeight: 44, fontWeight: 700 }}
            >
              {connectionLoading
                ? "Checking..."
                : isConnected
                ? "Unfriend"
                : hasIncomingRequest
                  ? "Accept Friend Request"
                  : hasOutgoingRequest
                    ? "Request Sent"
                    : "Add Friend"}
            </Button>
          ) : null}
        </Stack>
      </Box>

      {(isOwnProfile || isPublicPersonProfile) ? (
      <Box
        id="profile-content-section"
        sx={{
          mt: 3,
          scrollMarginTop: `calc(${APP_SHELL_LAYOUT.TOP_APP_BAR_OFFSET_CSS} + 12px)`,
          borderRadius: 1,
          transition: "background-color 180ms ease, box-shadow 180ms ease",
          backgroundColor: capsulesSectionHighlighted
            ? (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(86,195,114,0.08)"
                  : "rgba(38,19,74,0.04)"
            : "transparent",
          boxShadow: capsulesSectionHighlighted
            ? (theme) =>
                `0 0 0 1px ${
                  theme.palette.mode === "dark"
                    ? "rgba(86,195,114,0.28)"
                    : "rgba(38,19,74,0.14)"
                }`
            : "none",
        }}
      >
        {isOwnProfile ? (
          <>
            <SegmentedTabs
              value={ownProfileTab}
              options={ownProfileTabOptions}
              onChange={setOwnProfileTab}
            />

            {ownProfileTab === "notifications" ? (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.75 }}>
                  Friend Requests
                </Typography>
                {requestsLoading ? (
                  <ProfileAlertsSkeleton />
                ) : incomingRequests.length === 0 ? (
                  <Alert severity="info">No pending connection requests.</Alert>
                ) : (
                  <List sx={{ p: 0, mb: 1.75 }}>
                    {incomingRequests.map((row) => {
                      const handle =
                        normalizePublicHandle(row.publicHandle || "") ||
                        getPublicHandle(row.userName);
                      const displayName = ellipsizeProfileText(row.displayName || "Unnamed Profile");
                      const secondaryText = handle
                        ? `@${ellipsizeProfileText(handle)}`
                        : ellipsizeProfileText(row.email || "No public handle");
                      const profilePath = getPublicProfilePathByHandle(handle) || getProfilePathByUid(row.uid);

                      return (
                        <ListItem key={row.uid} data-testid="profile-alert-row" disablePadding sx={{ mb: 0.9 }}>
                          <Box
                            sx={{
                              width: "100%",
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 1,
                              p: 0.8,
                            }}
                          >
                            <ListItemButton
                              component={RouterLink}
                              to={profilePath}
                              sx={{ borderRadius: 1, px: 0.5, py: 0.4 }}
                            >
                              <Avatar src={row.profileImage || undefined} sx={{ mr: 1, width: 38, height: 38 }}>
                                {(displayName || handle || "P").charAt(0).toUpperCase()}
                              </Avatar>
                              <ListItemText
                                primary={displayName}
                                secondary={secondaryText}
                                primaryTypographyProps={{ fontWeight: 700 }}
                              />
                            </ListItemButton>

                            <Stack direction="row" spacing={1} sx={{ mt: 0.6, justifyContent: "flex-end" }}>
                              <Button
                                size="small"
                                color="inherit"
                                variant="outlined"
                                startIcon={<CloseRoundedIcon fontSize="small" />}
                                onClick={() => handleDeclineIncomingRequest(row)}
                              >
                                Decline
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<CheckRoundedIcon fontSize="small" />}
                                onClick={() => handleAcceptIncomingRequest(row)}
                              >
                                Accept
                              </Button>
                            </Stack>
                          </Box>
                        </ListItem>
                      );
                    })}
                  </List>
                )}

                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.75 }}>
                  Discussion Updates
                </Typography>
                {unreadCommentCapsules.length === 0 && unreadMentionActivities.length === 0 ? (
                  <Alert severity="success">No unread discussion updates.</Alert>
                ) : (
                  <List sx={{ p: 0 }}>
                    {unreadMentionActivities.map((item) => (
                      <ListItem key={item.id} data-testid="profile-alert-row" disablePadding sx={{ mb: 0.9 }}>
                        <ListItemButton
                          onClick={() => handleOpenMentionActivity(item)}
                          sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                            py: 1,
                          }}
                        >
                          <Avatar
                            sx={(theme) => ({
                              mr: 1.2,
                              width: 34,
                              height: 34,
                              bgcolor:
                                theme.palette.mode === "dark"
                                  ? "rgba(255,255,255,0.1)"
                                  : "rgba(38, 19, 74, 0.1)",
                              color: theme.palette.mode === "dark" ? "text.primary" : "text.secondary",
                            })}
                          >
                            <ForumRoundedIcon fontSize="small" />
                          </Avatar>
                          <ListItemText
                            primary={`${item.actorDisplayName} mentioned you`}
                            secondary={item.title}
                            primaryTypographyProps={{ fontWeight: 700 }}
                            secondaryTypographyProps={{
                              sx: { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
                            }}
                          />
                          <Box sx={{ ml: 1.2, color: "text.secondary", fontSize: "0.78rem", fontWeight: 700 }}>
                            {formatWhen(item.when)}
                          </Box>
                        </ListItemButton>
                      </ListItem>
                    ))}
                    {unreadCommentCapsules.map((item) => (
                      <ListItem key={item.capsuleId} data-testid="profile-alert-row" disablePadding sx={{ mb: 0.9 }}>
                        <ListItemButton
                          onClick={() => navigate(`/capsule/${item.capsuleId}`, { state: { source: "profile-notifications" } })}
                          sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                            py: 1,
                          }}
                        >
                          <Avatar
                            sx={(theme) => ({
                              mr: 1.2,
                              width: 34,
                              height: 34,
                              bgcolor:
                                theme.palette.mode === "dark"
                                  ? "rgba(255,255,255,0.1)"
                                  : "rgba(38, 19, 74, 0.1)",
                              color: theme.palette.mode === "dark" ? "text.primary" : "text.secondary",
                            })}
                          >
                            <ForumRoundedIcon fontSize="small" />
                          </Avatar>
                          <ListItemText
                            primary="New comment or reply"
                            secondary={item.title}
                            primaryTypographyProps={{ fontWeight: 700 }}
                            secondaryTypographyProps={{
                              sx: { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
                            }}
                          />
                          <Box sx={{ ml: 1.2, color: "text.secondary", fontSize: "0.78rem", fontWeight: 700 }}>
                            {formatWhen(item.when)}
                          </Box>
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                )}
              </>
            ) : null}
          </>
        ) : null}

        {isPublicPersonProfile ? (
          <SegmentedTabs
            value={publicProfileTab}
            options={publicProfileTabOptions}
            onChange={setPublicProfileTab}
          />
        ) : null}

        {(isOwnProfile && ownProfileTab === "businessFeed") ? (
          <>
            {businessFeedLoading && followedBusinessCards.length === 0 && followingContentItems.length === 0 ? (
              <ProfileBusinessFeedSkeleton />
            ) : followedBusinessCards.length === 0 && followingContentItems.length === 0 ? (
              <Alert severity="info">Follow businesses to see their coupons, raffles, and featured time capsules here.</Alert>
            ) : (
              <Stack spacing={1.5}>
                {followedBusinessCards.length ? (
                  <>
                    <Stack direction="row" alignItems="baseline" justifyContent="space-between">
                      <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                        Businesses you follow
                      </Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 800 }}>
                        Tap a logo
                      </Typography>
                    </Stack>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                        gap: 1.25,
                      }}
                    >
                      {followedBusinessCards.map((business) => {
                        const avatarAttrs = buildAvatarImageAttrs(business.avatarUrl, { size: 56 });
                        const badges = [
                          business.counts.coupon ? "C" : "",
                          business.counts.raffle ? "R" : "",
                          business.counts.capsule ? "T" : "",
                        ].filter(Boolean);
                        return (
                          <ButtonBase
                            key={business.id}
                            data-testid="profile-business-tile"
                            component={RouterLink}
                            to={`/business/${business.id}`}
                            sx={{
                              minWidth: 0,
                              display: "grid",
                              justifyItems: "center",
                              gap: 0.45,
                              textDecoration: "none",
                              color: "inherit",
                            }}
                          >
                            <Box sx={{ position: "relative" }}>
                              <Avatar
                                src={avatarAttrs.src || undefined}
                                imgProps={{
                                  srcSet: avatarAttrs.srcSet || undefined,
                                  sizes: avatarAttrs.sizes || undefined,
                                  loading: "lazy",
                                  decoding: "async",
                                }}
                                sx={{
                                  width: 56,
                                  height: 56,
                                  border: "2px solid",
                                  borderColor: (theme) =>
                                    business.counts.total
                                      ? theme.palette.success.main
                                      : theme.palette.mode === "dark"
                                        ? "rgba(255,255,255,0.14)"
                                        : "rgba(38,19,74,0.12)",
                                  ...getFallbackAvatarSx(Boolean(business.avatarUrl)),
                                }}
                              >
                                {renderAvatarFallback(business.name)}
                              </Avatar>
                              {business.counts.total ? (
                                <Box
                                  sx={(theme) => ({
                                    position: "absolute",
                                    top: 1,
                                    right: 1,
                                    width: 13,
                                    height: 13,
                                    borderRadius: "50%",
                                    backgroundColor: theme.palette.success.main,
                                    border: `2px solid ${theme.palette.background.default}`,
                                  })}
                                />
                              ) : null}
                            </Box>
                            <Typography
                              variant="caption"
                              sx={{
                                maxWidth: "100%",
                                color: "text.primary",
                                fontWeight: 800,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {business.name}
                            </Typography>
                            {badges.length ? (
                              <Stack direction="row" spacing={0.25}>
                                {badges.map((badge) => (
                                  <Box
                                    key={badge}
                                    component="span"
                                    sx={(theme) => ({
                                      px: 0.45,
                                      py: 0.1,
                                      borderRadius: 999,
                                      fontSize: "0.58rem",
                                      fontWeight: 900,
                                      lineHeight: 1.2,
                                      color: theme.palette.success.main,
                                      backgroundColor: alpha(theme.palette.success.main, 0.16),
                                    })}
                                  >
                                    {badge}
                                  </Box>
                                ))}
                              </Stack>
                            ) : null}
                          </ButtonBase>
                        );
                      })}
                    </Box>
                  </>
                ) : null}

                {followingContentItems.length ? (
                  <>
                    <Stack direction="row" alignItems="baseline" justifyContent="space-between">
                      <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                        Fresh from following
                      </Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 800 }}>
                        Coupons, raffles, capsules
                      </Typography>
                    </Stack>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                        gap: "3px",
                        borderRadius: 1,
                        overflow: "hidden",
                      }}
                    >
                      {followingContentItems.map((item) => (
                        <ButtonBase
                          key={item.id}
                          data-testid="profile-following-content-tile"
                          component={RouterLink}
                          to={item.href}
                          sx={(theme) => ({
                            position: "relative",
                            aspectRatio: "1 / 1",
                            alignItems: "stretch",
                            justifyContent: "stretch",
                            overflow: "hidden",
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? alpha(theme.palette.common.white, 0.08)
                                : alpha(theme.colors?.paleBlue || theme.colors?.mediumBlue || "#4164D8", 0.08),
                            textDecoration: "none",
                            color: "inherit",
                          })}
                        >
                          {item.imageUrl ? (
                            <Box component="img" src={item.imageUrl} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          ) : null}
                          <Box
                            sx={{
                              position: "absolute",
                              inset: "auto 0 0 0",
                              minHeight: 46,
                              px: 0.75,
                              py: 0.65,
                              background:
                                "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.58) 38%, rgba(0,0,0,0.86) 100%)",
                            }}
                          >
                            <Typography sx={{ color: "common.white", fontSize: 10.5, fontWeight: 900, lineHeight: 1.15, textAlign: "left", textShadow: "0 1px 2px rgba(0,0,0,0.75)" }}>
                              {item.label}
                            </Typography>
                            <Typography sx={{ mt: 0.2, color: "rgba(255,255,255,0.96)", fontSize: 10.5, lineHeight: 1.2, textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textShadow: "0 1px 2px rgba(0,0,0,0.75)" }}>
                              {item.title}
                            </Typography>
                          </Box>
                        </ButtonBase>
                      ))}
                    </Box>
                  </>
                ) : null}
              </Stack>
            )}
          </>
        ) : isPublicPersonProfile ? (
          publicProfileTab === "coupons" ? (
            publicOfferTabsLoading ? (
              <ProfileOfferListSkeleton testId="profile-coupons-skeleton" label="Loading coupons" />
            ) : (
              <ProfileClaimOfferList
                title="Coupons you can claim"
                emptyMessage="No valid coupons are available from this profile right now."
                items={publicProfileCouponItems}
                actionLabel="Claim"
              />
            )
          ) : publicProfileTab === "raffles" ? (
            publicOfferTabsLoading ? (
              <ProfileOfferListSkeleton testId="profile-raffles-skeleton" label="Loading raffles" />
            ) : (
              <ProfileClaimOfferList
                title="Raffles you can enter"
                emptyMessage="No open raffles are available from this profile right now."
                items={publicProfileRaffleItems}
                actionLabel="Enter"
              />
            )
          ) : (
            <>
              {lovedLoading ? (
                <ProfileTileGridSkeleton testId="profile-loved-skeleton" label="Loading loved capsules" />
              ) : publicLovedContentItems.length === 0 ? (
                <Alert severity="info">No public loved time capsules yet.</Alert>
              ) : (
                <Stack spacing={1}>
                  <Stack direction="row" alignItems="baseline" justifyContent="space-between">
                    <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                      Loved capsules
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 800 }}>
                      Public only
                    </Typography>
                  </Stack>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                      gap: "3px",
                      borderRadius: 1,
                      overflow: "hidden",
                    }}
                  >
                    {publicLovedContentItems.map((item) => (
                      <ButtonBase
                        key={item.id}
                        data-testid="profile-loved-tile"
                        component={RouterLink}
                        to={item.href}
                        sx={(theme) => ({
                          position: "relative",
                          aspectRatio: "1 / 1",
                          alignItems: "stretch",
                          justifyContent: "stretch",
                          overflow: "hidden",
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? alpha(theme.palette.common.white, 0.08)
                              : alpha(theme.colors?.paleBlue || theme.colors?.mediumBlue || "#4164D8", 0.08),
                          textDecoration: "none",
                          color: "inherit",
                        })}
                      >
                        {item.imageUrl ? (
                          <Box
                            component="img"
                            src={item.imageUrl}
                            alt=""
                            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        ) : null}
                        <Box
                          sx={{
                            position: "absolute",
                            inset: "auto 0 0 0",
                            minHeight: 46,
                            px: 0.75,
                            py: 0.65,
                            background:
                              "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.58) 38%, rgba(0,0,0,0.86) 100%)",
                          }}
                        >
                          <Typography
                            sx={{
                              color: "common.white",
                              fontSize: 10.5,
                              fontWeight: 900,
                              lineHeight: 1.15,
                              textAlign: "left",
                              textShadow: "0 1px 2px rgba(0,0,0,0.75)",
                            }}
                          >
                            {item.label}
                          </Typography>
                          <Typography
                            sx={{
                              mt: 0.2,
                              color: "rgba(255,255,255,0.96)",
                              fontSize: 10.5,
                              lineHeight: 1.2,
                              textAlign: "left",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              textShadow: "0 1px 2px rgba(0,0,0,0.75)",
                            }}
                          >
                            {item.title}
                          </Typography>
                        </Box>
                      </ButtonBase>
                    ))}
                  </Box>
                </Stack>
              )}
            </>
          )
        ) : null}
      </Box>
      ) : null}
      <Snackbar
        open={Boolean(notificationsSnackbar)}
        autoHideDuration={3000}
        onClose={() => setNotificationsSnackbar("")}
      >
        <Alert onClose={() => setNotificationsSnackbar("")} variant="filled" severity="info" sx={{ width: "100%" }}>
          {notificationsSnackbar}
        </Alert>
      </Snackbar>
      <Dialog
        open={unfriendConfirmOpen}
        onClose={() => {
          if (!connectionBusy) setUnfriendConfirmOpen(false);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Unfriend {profileDisplayName}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to unfriend {profileDisplayName}?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setUnfriendConfirmOpen(false)} disabled={connectionBusy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmUnfriend}
            disabled={connectionBusy}
          >
            Unfriend
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
